import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FIREBASE_ENV } from './firebase';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import PublicOnboarding from './components/PublicOnboarding';
import PublicActivate from './components/PublicActivate';
import FamilyOnboarding from './components/FamilyOnboarding';
import BillingReturn from './components/BillingReturn';
import FleetJoinPage from './components/FleetJoinPage';
import { PayPage } from './components/SubscriptionGate';
import FirebaseTest from './components/FirebaseTest';
import Reports from './components/Reports';
import OrgSettingsPage from './components/OrgSettingsPage';
import ActivityTracker from './components/ActivityTracker';
import PublicTrack from './components/PublicTrack';
import AcceptConsoleInvite from './components/AcceptConsoleInvite';
import TripConductorReveal from './components/TripConductorReveal';
import ConsoleAdmins from './components/ConsoleAdmins';
import FleetPortalRouter from './fleet/FleetPortalRouter';
import TrasseConsole from './console/TrasseConsole';
import ConsoleContainer from './console/ConsoleContainer';
import { SITE_PAGES, SitePageRoute } from './site-v3/SiteRoutes';
import { safeNext } from './utils/safeNext';
import './App.css';

// Serves a standalone full-page static doc from /public (marketing & support
// pages). These live as complete HTML files so they render before the SPA
// boots and stay reachable via clean in-app URLs (/marketing, /support).
const StaticPage: React.FC<{ file: string }> = ({ file }) => {
  React.useEffect(() => {
    window.location.replace(file);
  }, [file]);
  return null;
};

/** Shown while the saved session loads — a blank page rather than a flash
 *  of the sign-in form. */
const AuthWait: React.FC = () => <div style={{ minHeight: '100vh' }} aria-busy="true" />;

/** Not signed in: the new sign-in page, carrying the page asked for so
 *  signing in returns there instead of to Overview. */
const ToSignIn: React.FC = () => {
  const loc = useLocation();
  const next = loc.pathname + loc.search;
  return <Navigate to={next === '/' ? '/sign-in' : `/sign-in?next=${encodeURIComponent(next)}`} replace />;
};

// Protected Route Component - requires any admin (PEAR or fleet)
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, isAdmin, authReady } = useAuth();

  if (!authReady) return <AuthWait />;
  if (!currentUser || !isAdmin) {
    return <ToSignIn />;
  }

  return <>{children}</>;
};

// PEAR-only routes - fleet admins cannot access; redirect to their fleet dashboard
const PearOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, isAdmin, isFleetAdmin, authReady } = useAuth();

  if (!authReady) return <AuthWait />;
  if (!currentUser || !isAdmin) {
    return <ToSignIn />;
  }
  if (isFleetAdmin) {
    return <Navigate to="/fleet" replace />;
  }
  return <>{children}</>;
};

// Fleet-only route - PEAR admins cannot access; redirect to main dashboard
const FleetOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, isAdmin, isFleetAdmin, authReady } = useAuth();

  if (!authReady) return <AuthWait />;
  if (!currentUser || !isAdmin) {
    return <ToSignIn />;
  }
  if (!isFleetAdmin) {
    return <Navigate to="/admin" replace />;
  }
  return <>{children}</>;
};

/** The household console's rooms. Every one renders the portal router, which
 *  decides between the family shell and a redirect back to /fleet. */
const FAMILY_ROUTES = [
  '/families',
  '/families/analytics',
  '/families/reports',
  '/families/hours',
  '/families/billing',
  '/families/settings',
];

// Fleet admins land on bus tracking; PEAR admins on admin dashboard — or on
// the page they were sent to sign in from (?next=).
const HomeRedirect: React.FC = () => {
  const { isFleetAdmin } = useAuth();
  const [params] = useSearchParams();
  return <Navigate to={safeNext(params.get('next')) || (isFleetAdmin ? '/fleet' : '/admin')} replace />;
};

/** /login: forwards a signed-in admin to their console. The old page still
 *  serves "Forgot?" and "Create your account" (?mode=forgot, ?mode=signup);
 *  plain sign-in is the new page, /sign-in (Gabriel, 2026-10-28). */
const LoginRoute: React.FC = () => {
  const { currentUser, isAdmin, authReady } = useAuth();
  const [params] = useSearchParams();
  if (!authReady) return <AuthWait />;
  if (currentUser && isAdmin) return <HomeRedirect />;
  const mode = params.get('mode');
  if (mode === 'signup' || mode === 'forgot') return <Login />;
  const next = safeNext(params.get('next'));
  return <Navigate to={next ? `/sign-in?next=${encodeURIComponent(next)}` : '/sign-in'} replace />;
};

// Main App Component
const AppContent: React.FC = () => {
  return (
    <Router>
      <div className="App">
        <Routes>
          {/* Public routes */}
          {/* trasseapp.com — the marketing site (redesign, 2026-10-23). */}
          {SITE_PAGES.map(({ path, page }) => (
            <Route key={path} path={path} element={<SitePageRoute page={page} />} />
          ))}
          <Route path="/console-preview" element={<TrasseConsole />} />
          <Route path="/onboarding" element={<PublicOnboarding />} />
          {/* Trasse Families signs up on its own page: no plan picker, no card. */}
          <Route path="/families/start" element={<FamilyOnboarding />} />
          <Route path="/activate" element={<PublicActivate />} />
          <Route path="/billing/return" element={<BillingReturn />} />
          <Route path="/join" element={<FleetJoinPage />} />
          <Route path="/pay" element={<PayPage />} />
          {/* The old demo dashboards: nothing on the new site links to them, and
              How it works shows the product as it is now (2026-10-28). */}
          <Route path="/demo/bus-tracking" element={<Navigate to="/how-it-works" replace />} />
          <Route path="/demo/lorry" element={<Navigate to="/how-it-works" replace />} />
          <Route path="/track/:token" element={<PublicTrack />} />
          <Route path="/console/accept" element={<AcceptConsoleInvite />} />
          <Route path="/trip/reveal" element={<TripConductorReveal />} />
          {/* The old marketing page is the new site's home now (2026-10-28). */}
          <Route path="/marketing" element={<Navigate to="/" replace />} />
          <Route path="/support" element={<StaticPage file="/support.html" />} />
          <Route path="/privacy" element={<StaticPage file="/privacy.html" />} />
          <Route path="/terms" element={<StaticPage file="/terms.html" />} />
          <Route path="/login" element={<LoginRoute />} />

          {/* PEAR Super Admin routes */}
          <Route
            path="/admin"
            element={
              <PearOnlyRoute>
                <ConsoleContainer />
              </PearOnlyRoute>
            }
          />
          {/* Legacy console kept reachable as a fallback during the facelift. */}
          <Route
            path="/admin/legacy"
            element={
              <PearOnlyRoute>
                <Dashboard />
              </PearOnlyRoute>
            }
          />
          <Route
            path="/admin/org/:orgCode/settings"
            element={
              <PearOnlyRoute>
                <OrgSettingsPage />
              </PearOnlyRoute>
            }
          />
          <Route
            path="/test"
            element={
              <PearOnlyRoute>
                <FirebaseTest />
              </PearOnlyRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <PearOnlyRoute>
                <Reports />
              </PearOnlyRoute>
            }
          />
          <Route
            path="/activity-tracker"
            element={
              <PearOnlyRoute>
                <ActivityTracker />
              </PearOnlyRoute>
            }
          />
          <Route
            path="/admin/console-admins"
            element={
              <PearOnlyRoute>
                <ConsoleAdmins />
              </PearOnlyRoute>
            }
          />

          {/* Fleet admin route */}
          <Route
            path="/fleet/*"
            element={
              <FleetOnlyRoute>
                <FleetPortalRouter />
              </FleetOnlyRoute>
            }
          />
          <Route path="/bus-tracking" element={<Navigate to="/fleet" replace />} />

          {/* Trasse Families household console. Same guard as /fleet — the portal
              router sends a FAMILIES org here and everyone else back to /fleet. */}
          {FAMILY_ROUTES.map((path) => (
            <Route
              key={path}
              path={path}
              element={
                <FleetOnlyRoute>
                  <FleetPortalRouter />
                </FleetOnlyRoute>
              }
            />
          ))}

          {/* Anything else: the site's own "page not found". */}
          <Route path="*" element={<SitePageRoute page="NotFound" />} />
        </Routes>
      </div>
    </Router>
  );
};

/* A build pointed at the TEST project says so on every page, on every screen
   size, so a test site is never mistaken for the real one. Production builds
   render nothing here (2026-10-08). */
const TestEnvironmentStripe: React.FC = () => {
  if (FIREBASE_ENV !== 'test') return null;
  return (
    <div
      role="status"
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100000,
        background: '#b45309', color: '#fff', textAlign: 'center',
        font: '600 12px/20px system-ui, sans-serif', letterSpacing: '0.04em',
        pointerEvents: 'none',
      }}
    >
      TEST ENVIRONMENT — trasse-test, not production
    </div>
  );
};

// App with Auth Provider
const App: React.FC = () => {
  return (
    <AuthProvider>
      <TestEnvironmentStripe />
      <AppContent />
    </AuthProvider>
  );
};

export default App;
